/**
 * Actycrea: pin.esm.js (ES6)
 * GSAP + ScrollTrigger
 * MIT
 */
import { PREFIX } from '../helpers/config.js';
import { getCurrentBreakpoint } from '../helpers/breakpoints.js';
import { EVENT_RESIZE_INIT, EVENT_RESIZE_END } from '../helpers/events.js';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin( ScrollTrigger );

const SELECTOR      = `data-${PREFIX}-mypn`;
const SELECTOR_DATA = `[${SELECTOR}]`;

const DEFAULTS = {
  target      : '[data-pin]', // ítems dentro del contenedor (hijos directos recomendado)
  top         : 0,            // offset px desde top viewport
  scrub       : true,
  pinSpacing  : false,        // no crear spacer; el alto lo define el wrapper
  anticipate  : 0,
  markers     : false,
  z           : 1040 ,
  bp          : [ 'xl', 'xxl' ]
};

export class MyPN {
  constructor( element , config = { } ){
    if( !(element instanceof HTMLElement) ) throw new Error('MyPN: element inválido');

    this._element = element;
    this._config  = { ...DEFAULTS , ...config };
    this._enabled  = false;
    this._triggers = [];

    this._onResizeInit = () => this.destroy();
    this._onResizeEnd  = () => { if( this._shouldEnable() ) this._setup(); };

    window.addEventListener( EVENT_RESIZE_INIT.type , this._onResizeInit );
    window.addEventListener( EVENT_RESIZE_END.type  , this._onResizeEnd );

    if( this._shouldEnable() ) this._setup();
  }

  _shouldEnable(){
    return this._config.bp.includes( getCurrentBreakpoint( ) );
  }

  _setup(){
    if( this._enabled ) return;
    const items = Array.from( this._element.querySelectorAll( this._config.target ) );
    if( !items.length ) return;

    items.forEach( (fig, i) => {
      const nextFig = items[ i + 1 ] || null;

      const t = ScrollTrigger.create({
        trigger            : fig,
        start              : () => `top top+=${this._config.top}`,
        end                : () => {
          const wrapper = this._element;
          const relTop = (el) => {
            const rw = wrapper.getBoundingClientRect();
            const re = el.getBoundingClientRect();
            return (re.top - rw.top) + wrapper.scrollTop;
          };
          const figTop = relTop( fig );
          const figH   = fig.offsetHeight;
          const dist   = nextFig
            ? (relTop(nextFig) - figTop - figH)
            : (wrapper.scrollHeight - figTop - figH);

          return `+=${ Math.max(1, Math.round(dist)) }`;
        },
        pin                : true,
        pinSpacing         : this._config.pinSpacing,
        scrub              : this._config.scrub,
        anticipatePin      : this._config.anticipate,
        markers            : this._config.markers,
        pinType            : 'fixed',
        pinReparent        : true,
        onToggle           : (self) => {
          if( self.isActive ){
            fig.classList.add('is-pinned');
            gsap.set( fig , { zIndex: this._config.z } );
          } else {
            fig.classList.remove('is-pinned');
            gsap.set( fig , { clearProps: 'z-index' } );
          }
        },
        invalidateOnRefresh: true
      });

      this._triggers.push( t );
    } );

    // refresh tras imágenes
    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener( 'load', refresh, { once: true } );
    this._element.querySelectorAll( 'img' ).forEach( img => {
      if( img.complete ) return;
      img.addEventListener( 'load', refresh, { once: true } );
    } );

    this._enabled = true;
    ScrollTrigger.refresh( );
  }

  destroy(){
    if( !this._enabled ) return;
    this._triggers.forEach( t => t.kill() );
    this._triggers = [];
    this._enabled = false;
  }

  // limpieza completa si desmontas el nodo
  dispose(){
    this.destroy();
    window.removeEventListener( EVENT_RESIZE_INIT.type , this._onResizeInit );
    window.removeEventListener( EVENT_RESIZE_END.type  , this._onResizeEnd );
  }


  static initAll( root = document ){
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [];
    nodes.forEach( el => {
      if( el._mypnInstance ) return;
      let cfg = {};
      const raw = el.getAttribute( SELECTOR );
      if( raw ){ try{ cfg = JSON.parse( raw ); }catch{ cfg = {}; } }
      el._mypnInstance = new MyPN( el , cfg );
      instances.push( el._mypnInstance );
    } );
    return instances;
  }
}

export default MyPN;
